'use client';

import React from 'react';
import * as Sentry from '@sentry/nextjs';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    Sentry.captureException(error, {
      tags: {
        errorType: 'react-error-boundary',
      },
      extra: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      },
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

function DefaultErrorFallback({ error, resetError }: { error?: Error; resetError: () => void }) {
  return (
    <div className="flex min-h-[400px] items-center justify-center">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">出現錯誤</h2>
        <p className="text-sm text-gray-600 mb-4">
          抱歉，頁面載入時發生錯誤。請稍後再試。
        </p>
        {error && (
          <pre className="text-xs text-red-600 mb-4 bg-red-50 p-2 rounded max-w-md overflow-auto">
            {error.message}
            {process.env.NODE_ENV === 'development' && error.stack && (
              <div className="mt-2 pt-2 border-t border-red-200">
                <div className="font-semibold">Stack trace:</div>
                {error.stack}
              </div>
            )}
          </pre>
        )}
        <button
          onClick={resetError}
          className="bg-primaryColor text-white px-4 py-2 rounded text-sm hover:bg-primaryColor/90"
        >
          重新載入
        </button>
      </div>
    </div>
  );
}